import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  FaBolt,
  FaBuilding,
  FaChevronDown,
  FaChevronRight,
  FaDoorOpen,
  FaDownload,
  FaGraduationCap,
  FaPlayCircle,
  FaSearch,
  FaSync,
  FaUsers,
} from "react-icons/fa";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { EnvToggle } from "../OrgUsageAnalytics/components/EnvToggle";
import { useEnv } from "../OrgUsageAnalytics/useEnv";
import {
  dateRangeParams,
  fetchClientCrm,
  fetchClientRecommendation,
  fetchClientTracking,
} from "./api";
import { useReducedMotion } from "./useReducedMotion";
import { MetricCard } from "./components/MetricCard";
import { Sparkline } from "./components/Sparkline";
import { TrendBadge } from "./components/TrendBadge";
import { DayHeatStrip } from "./components/DayHeatStrip";
import { RiskBadge } from "./components/RiskBadge";
import { ClientDetail } from "./components/ClientDetail";
import {
  downloadCsv,
  formatDayLabel,
  formatNumber,
  formatRelative,
  formatWeekLabel,
  sortClients,
  toDailyCsv,
  toSummaryCsv,
} from "./selectors";
import "./ClientTracking.css";

const RANGES = [
  { days: 7, label: "7 days" },
  { days: 14, label: "14 days" },
  { days: 30, label: "30 days" },
  { days: 90, label: "90 days" },
];

/**
 * Columns whose numbers only make sense next to the metric definition, so the
 * definition rides along as the header tooltip rather than living in a legend
 * nobody scrolls to.
 */
const TABLE_COLUMNS = [
  { key: "name", label: "Client", align: "left" },
  {
    key: "riskScore",
    label: "Health",
    hint: "Rule-based score from usage, adoption and CRM signals. Open a row to see exactly what drove it.",
  },
  { key: "seats", label: "Seats", hint: "Non-Addy members of the organization." },
  {
    key: "activeUsers",
    label: "Active users",
    hint: "Distinct people with any activity in the window.",
  },
  {
    key: "adoptionRate",
    label: "Adoption",
    hint: "Active users as a share of seats. Blank when the org has no members on record.",
  },
  {
    key: "accesses",
    label: "Accesses",
    hint: "Loans opened — the closest signal to someone actually working in the product.",
  },
  {
    key: "accessesPerUser",
    label: "Per user",
    hint: "Accesses divided by active users.",
  },
  {
    key: "manualRuns",
    label: "Manual runs",
    hint: "Checklist runs a person started. Auto-triggered runs are excluded.",
  },
  {
    key: "trainingInstructions",
    label: "Training",
    hint: "Train-your-agent instructions written in the window.",
  },
  {
    key: "activeDays",
    label: "Active days",
    hint: "Days in the window with any activity, and which days those were.",
  },
  { key: "growth", label: "Trend", hint: "Accesses vs the equally long window before this one." },
  { key: "lastActiveAt", label: "Last active" },
];

const CHART_SERIES = [
  { key: "accesses", label: "Accesses", color: "#2563eb" },
  { key: "manualRuns", label: "Manual runs", color: "#059669" },
  { key: "activeUsers", label: "Active users", color: "#d97706" },
  { key: "trainingInstructions", label: "Training instructions", color: "#7c3aed" },
];

function SkeletonRows({ columns }) {
  return (
    <>
      {Array.from({ length: 8 }).map((_, rowIndex) => (
        <tr key={rowIndex}>
          {Array.from({ length: columns }).map((__, cellIndex) => (
            <td key={cellIndex}>
              <span className="ct-skel" />
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}

export default function ClientTrackingPage({ onReady }) {
  const [env, setEnv] = useEnv();
  const reducedMotion = useReducedMotion();
  const [days, setDays] = useState(14);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [activeOnly, setActiveOnly] = useState(true);
  const [includeInternal, setIncludeInternal] = useState(false);
  const [crmByOrg, setCrmByOrg] = useState({});
  const [recByOrg, setRecByOrg] = useState({});
  const [grain, setGrain] = useState("daily");
  const [sortKey, setSortKey] = useState("riskScore");
  const [sortDirection, setSortDirection] = useState("desc");
  const [expanded, setExpanded] = useState(null);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(timer);
  }, [search]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const json = await fetchClientTracking(
        {
          ...dateRangeParams(days),
          search: debouncedSearch,
          activeOnly: activeOnly ? "true" : "false",
          includeInternal: includeInternal ? "true" : "false",
          limit: "300",
        },
        env,
      );
      setData(json);
    } catch (err) {
      toast.error(err.message || "Failed to load client tracking");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [days, debouncedSearch, activeOnly, includeInternal, env]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    onReady?.();
  }, [onReady]);

  const clients = useMemo(
    () => sortClients(data?.clients || [], sortKey, sortDirection),
    [data, sortKey, sortDirection],
  );

  const totals = data?.totals || {};
  const chartData = useMemo(() => {
    const series = grain === "weekly" ? data?.weekly || [] : data?.daily || [];
    return series.map((point) => ({
      ...point,
      label:
        grain === "weekly" ? formatWeekLabel(point) : formatDayLabel(point.day),
    }));
  }, [data, grain]);

  /**
   * Live HubSpot lookup for one client. Cached per organization for the life of
   * the page so collapsing and reopening a row does not re-spend API calls.
   */
  const loadCrm = useCallback(
    async (organizationId) => {
      setCrmByOrg((current) => ({
        ...current,
        [organizationId]: { loading: true, result: current[organizationId]?.result },
      }));
      try {
        const result = await fetchClientCrm(organizationId, env);
        setCrmByOrg((current) => ({
          ...current,
          [organizationId]: { loading: false, result },
        }));
      } catch (err) {
        setCrmByOrg((current) => ({
          ...current,
          [organizationId]: {
            loading: false,
            result: { found: false, reason: err.message || "HubSpot lookup failed." },
          },
        }));
      }
    },
    [env],
  );

  const generateRecommendation = useCallback(
    async (organizationId) => {
      setRecByOrg((current) => ({
        ...current,
        [organizationId]: { loading: true, error: null, result: current[organizationId]?.result },
      }));
      try {
        const result = await fetchClientRecommendation(
          organizationId,
          dateRangeParams(days),
          env,
        );
        if (!result?.success) {
          throw new Error(result?.reason || "Could not generate a recommendation.");
        }
        setRecByOrg((current) => ({
          ...current,
          [organizationId]: { loading: false, error: null, result },
        }));
      } catch (err) {
        setRecByOrg((current) => ({
          ...current,
          [organizationId]: {
            loading: false,
            error: err.message || "Could not generate a recommendation.",
            result: null,
          },
        }));
      }
    },
    [days, env],
  );

  /**
   * Opening a row kicks off the CRM lookup once. The recommendation is NOT
   * auto-run — it costs a model call, and browsing the table should never
   * quietly spend them.
   */
  const toggleRow = (organizationId) => {
    const opening = expanded !== organizationId;
    setExpanded(opening ? organizationId : null);
    if (opening && !crmByOrg[organizationId]) loadCrm(organizationId);
  };

  const onSort = (key) => {
    if (key === sortKey) {
      setSortDirection((current) => (current === "desc" ? "asc" : "desc"));
      return;
    }
    setSortKey(key);
    setSortDirection(key === "name" ? "asc" : "desc");
  };

  const rangeLabel = data?.range?.startDay
    ? `${formatDayLabel(data.range.startDay)} – ${formatDayLabel(data.range.endDay)}`
    : `Last ${days} days`;

  const exportSummary = () => {
    if (!clients.length) return;
    downloadCsv(`client-tracking-${days}d.csv`, toSummaryCsv(clients));
  };

  const exportDaily = () => {
    if (!clients.length) return;
    downloadCsv(`client-tracking-daily-${days}d.csv`, toDailyCsv(clients));
  };

  return (
    <div className="ct-page">
      <ToastContainer position="top-right" autoClose={5000} />

      <header className="ct-hero">
        <div className="ct-hero-copy">
          <div className="ct-eyebrow">
            <FaBuilding aria-hidden="true" />
            Client tracking
          </div>
          <h1 className="ct-title">Client usage &amp; evolution</h1>
          <p className="ct-subtitle">
            Every active client, how many of their people showed up, what they
            did, and whether that is growing day by day and week by week.
          </p>
          <div className="ct-chip-row">
            <span className="ct-chip">{rangeLabel}</span>
            <span className="ct-chip">
              {formatNumber(data?.totalCount ?? 0)} clients matched
            </span>
            <span className="ct-chip ct-chip--muted">
              Compared against the previous {days} days
            </span>
            {data?.internalCount && !includeInternal ? (
              <span className="ct-chip ct-chip--muted">
                {formatNumber(data.internalCount)} internal orgs hidden
              </span>
            ) : null}
          </div>
        </div>
        <div className="ct-hero-actions">
          <EnvToggle env={env} onChange={setEnv} />
          <div className="ct-segment">
            {RANGES.map((range) => (
              <button
                key={range.days}
                type="button"
                className={days === range.days ? "is-active" : ""}
                onClick={() => setDays(range.days)}
              >
                {range.label}
              </button>
            ))}
          </div>
          <button
            type="button"
            className="ct-btn ct-btn--solid"
            onClick={load}
            disabled={loading}
          >
            <FaSync className={loading ? "fa-spin" : ""} aria-hidden="true" />
            Refresh
          </button>
        </div>
      </header>

      {data?.partial ? (
        <div className="ct-banner ct-banner--warn">
          Some queries timed out, so these numbers are incomplete. Try a shorter
          range or refresh.
        </div>
      ) : null}
      {data?.truncated ? (
        <div className="ct-banner ct-banner--warn">
          This window exceeded the usage-rollup read cap, so the oldest days may
          be under-counted. Narrow the range for exact numbers.
        </div>
      ) : null}

      <section className="ct-metrics">
        <MetricCard
          icon={FaBuilding}
          label="Active clients"
          value={totals.activeClients}
          hint={`of ${formatNumber(totals.clients ?? 0)} matched`}
          accent="accent"
        />
        <MetricCard
          icon={FaUsers}
          label="Active users"
          value={totals.activeUsers}
          hint={`of ${formatNumber(totals.seats ?? 0)} seats`}
          changePct={totals.deltas?.activeUsers}
          accent="amber"
        />
        <MetricCard
          icon={FaDoorOpen}
          label="Accesses"
          value={totals.accesses}
          hint="loans opened"
          changePct={totals.deltas?.accesses}
          accent="accent"
        />
        <MetricCard
          icon={FaPlayCircle}
          label="Manual runs"
          value={totals.manualRuns}
          hint="human-started"
          changePct={totals.deltas?.manualRuns}
          accent="green"
        />
        <MetricCard
          icon={FaGraduationCap}
          label="Training instructions"
          value={totals.trainingInstructions}
          hint="written in window"
          changePct={totals.deltas?.trainingInstructions}
          accent="violet"
        />
        <MetricCard
          icon={FaBolt}
          label="Total events"
          value={totals.events}
          hint="all activity"
          changePct={totals.deltas?.events}
          accent="cyan"
        />
      </section>

      <section className="ct-card">
        <header className="ct-card-head">
          <div>
            <h2>Evolution</h2>
            <p>
              {grain === "weekly"
                ? "Weekly totals across every matched client. Weeks start Monday (UTC)."
                : "Daily totals across every matched client (UTC days)."}
            </p>
          </div>
          <div className="ct-segment ct-segment--sm">
            <button
              type="button"
              className={grain === "daily" ? "is-active" : ""}
              onClick={() => setGrain("daily")}
            >
              Daily
            </button>
            <button
              type="button"
              className={grain === "weekly" ? "is-active" : ""}
              onClick={() => setGrain("weekly")}
            >
              Weekly
            </button>
          </div>
        </header>
        <div className="ct-chart">
          {chartData.length === 0 ? (
            <div className="ct-chart-empty">
              {loading ? "Loading activity…" : "No activity in this window."}
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={chartData} margin={{ top: 8, right: 12, bottom: 0, left: -12 }}>
                <defs>
                  {CHART_SERIES.map((series) => (
                    <linearGradient
                      key={series.key}
                      id={`ct-grad-${series.key}`}
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop offset="0%" stopColor={series.color} stopOpacity={0.28} />
                      <stop offset="100%" stopColor={series.color} stopOpacity={0.02} />
                    </linearGradient>
                  ))}
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--ct-track)" vertical={false} />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 11, fill: "var(--ct-muted)" }}
                  tickLine={false}
                  axisLine={{ stroke: "var(--ct-border)" }}
                  minTickGap={16}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: "var(--ct-muted)" }}
                  tickLine={false}
                  axisLine={false}
                  allowDecimals={false}
                />
                <RechartsTooltip
                  contentStyle={{
                    background: "var(--ct-surface)",
                    border: "1px solid var(--ct-border-strong)",
                    borderRadius: 10,
                    fontSize: 12,
                    color: "var(--ct-text)",
                  }}
                />
                <Legend wrapperStyle={{ fontSize: 12, paddingTop: 6 }} />
                {CHART_SERIES.map((series) => (
                  <Area
                    key={series.key}
                    type="monotone"
                    dataKey={series.key}
                    name={series.label}
                    stroke={series.color}
                    fill={`url(#ct-grad-${series.key})`}
                    strokeWidth={2}
                    dot={false}
                    activeDot={{ r: 3 }}
                    isAnimationActive={!reducedMotion}
                  />
                ))}
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>
      </section>

      <section className="ct-card">
        <header className="ct-card-head ct-card-head--tools">
          <div>
            <h2>Clients</h2>
            <p>Click a row to see its day-by-day and week-by-week detail.</p>
          </div>
          <div className="ct-tools">
            <label className="ct-search">
              <FaSearch aria-hidden="true" />
              <input
                type="search"
                value={search}
                placeholder="Search client name or id"
                onChange={(event) => setSearch(event.target.value)}
              />
            </label>
            <label className="ct-toggle">
              <input
                type="checkbox"
                checked={activeOnly}
                onChange={(event) => setActiveOnly(event.target.checked)}
              />
              Active only
            </label>
            <label
              className="ct-toggle"
              title="Addy's own and test organizations are excluded by default — they are among the most active tenants and distort every total."
            >
              <input
                type="checkbox"
                checked={includeInternal}
                onChange={(event) => setIncludeInternal(event.target.checked)}
              />
              Include internal
            </label>
            <button
              type="button"
              className="ct-btn"
              onClick={exportSummary}
              disabled={!clients.length}
            >
              <FaDownload aria-hidden="true" /> Summary CSV
            </button>
            <button
              type="button"
              className="ct-btn"
              onClick={exportDaily}
              disabled={!clients.length}
            >
              <FaDownload aria-hidden="true" /> Daily CSV
            </button>
          </div>
        </header>

        <div className="ct-table-wrap">
          <table className="ct-table">
            <thead>
              <tr>
                <th className="ct-th-toggle" aria-label="Expand" />
                {TABLE_COLUMNS.map((column) => (
                  <th
                    key={column.key}
                    className={`${column.align === "left" ? "ct-th-left" : ""}${
                      sortKey === column.key ? " is-sorted" : ""
                    }`}
                    title={column.hint}
                  >
                    <button type="button" onClick={() => onSort(column.key)}>
                      {column.label}
                      {sortKey === column.key ? (
                        <span aria-hidden="true">{sortDirection === "desc" ? " ↓" : " ↑"}</span>
                      ) : null}
                    </button>
                  </th>
                ))}
                <th className="ct-th-left">Daily shape</th>
              </tr>
            </thead>
            <tbody>
              {loading && !data ? (
                <SkeletonRows columns={TABLE_COLUMNS.length + 2} />
              ) : null}

              {!loading && clients.length === 0 ? (
                <tr>
                  <td colSpan={TABLE_COLUMNS.length + 2} className="ct-empty-cell">
                    No clients match this window and filter.
                  </td>
                </tr>
              ) : null}

              {clients.map((client) => {
                const isOpen = expanded === client.organizationId;
                const accessSeries = (client.daily || []).map((point) => point.accesses);

                return (
                  <React.Fragment key={client.organizationId}>
                    <tr
                      className={`ct-row${isOpen ? " is-open" : ""}`}
                      onClick={() => toggleRow(client.organizationId)}
                    >
                      <td className="ct-td-toggle">
                        {isOpen ? <FaChevronDown /> : <FaChevronRight />}
                      </td>
                      <td className="ct-td-name">
                        <span className="ct-client-name">{client.organizationName}</span>
                        {/* Plan reads better than a raw id in a health table,
                            but the id is what people copy to look the org up
                            elsewhere — so it stays available on hover. */}
                        <span
                          className="ct-client-id"
                          title={client.organizationId}
                        >
                          {client.crm?.addy_plan_status
                            ? `${client.crm.addy_plan || "Plan"} · ${client.crm.addy_plan_status}`
                            : client.organizationId}
                        </span>
                      </td>
                      <td>
                        <RiskBadge
                          level={client.riskLevel}
                          score={client.riskScore}
                          signalCount={client.signals?.length}
                        />
                      </td>
                      <td>{formatNumber(client.seats)}</td>
                      <td>{formatNumber(client.activeUsers)}</td>
                      <td>
                        {client.adoptionRate === null ||
                        client.adoptionRate === undefined ? (
                          <span
                            className="ct-adoption ct-adoption--unknown"
                            title="This organization has no members on record, so there is no denominator to divide by. It is not 0% adoption."
                          >
                            —
                          </span>
                        ) : (
                          <span
                            className="ct-adoption"
                            title={
                              client.adoptionRate > 100
                                ? `${client.activeUsers} people were active but only ${client.seats} seats are on record — the member roster looks stale.`
                                : undefined
                            }
                          >
                            <span className="ct-adoption-track">
                              <span
                                className="ct-adoption-fill"
                                style={{ width: `${Math.min(100, client.adoptionRate)}%` }}
                              />
                            </span>
                            {client.adoptionRate}%
                            {client.adoptionRate > 100 ? (
                              <span className="ct-adoption-flag" aria-hidden="true">!</span>
                            ) : null}
                          </span>
                        )}
                      </td>
                      <td className="ct-td-strong">{formatNumber(client.accesses)}</td>
                      <td>{formatNumber(client.accessesPerUser)}</td>
                      <td>{formatNumber(client.manualRuns)}</td>
                      <td>
                        {formatNumber(client.trainingInstructions)}
                        {client.trainingInstructionsAllTime ? (
                          <span
                            className="ct-sub-value"
                            title="All-time training instructions"
                          >
                            /{formatNumber(client.trainingInstructionsAllTime)}
                          </span>
                        ) : null}
                      </td>
                      <td className="ct-td-activedays">
                        <span className="ct-activedays-count">
                          {formatNumber(client.activeDays)}
                          <span className="ct-sub-value">/{client.daily?.length || 0}</span>
                        </span>
                        <DayHeatStrip points={client.daily} metric="events" metricLabel="events" />
                      </td>
                      <td>
                        <TrendBadge pct={client.deltas?.accesses} label="Accesses" />
                      </td>
                      <td className="ct-td-muted">{formatRelative(client.lastActiveAt)}</td>
                      <td>
                        <Sparkline
                          values={accessSeries}
                          title={`${client.organizationName} accesses per day`}
                        />
                      </td>
                    </tr>
                    {isOpen ? (
                      <tr className="ct-detail-row">
                        <td colSpan={TABLE_COLUMNS.length + 2}>
                          <ClientDetail
                            client={client}
                            crmState={crmByOrg[client.organizationId]}
                            recommendationState={recByOrg[client.organizationId]}
                            onFetchCrm={() => loadCrm(client.organizationId)}
                            onGenerate={() => generateRecommendation(client.organizationId)}
                          />
                        </td>
                      </tr>
                    ) : null}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
